import React from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';

import { 
  Logger, 
}                               from 'aws-amplify';

import {
  PlayArrow as StartCrawlIcon,
  Stop as StopCrawlIcon,
}                               from '@material-ui/icons';


import { 
  BucketIcon,
}                               from '../../components/aws';

import { 
  fmtStorageBytes,
  fmtStorageObjects,
  useStyles,
}                               from '../../components/storage';

import { 
  fmtClientAcl,
  fmtClientAclPrincipal,
  sortClientAclPrincipals,
}                               from '../../components/security';

import {
  fmtCrawlStatus,
}                               from '../../components/dataset';

import {
  startResource as startCrawl,
  stopResource as stopCrawl,
}                               from  '../../components/resource/hooks';

import { 
  fmtDaysHrsMinFromMins,
  fmtDaysHrsMinFromSecs,
  fmtIso8601AsLocalDateHrsMin,
  fmtS3ConsoleLink,
}                               from '../../components/utils/formatters';

import { 
  numericIntervalColumnFilterProps 
}                               from '../../components/utils/Mui.js';

import { 
  compileColumnDefs,
}                               from '../../components/utils/helpers';

import { 
  UserContext,
}                               from '../../components/user';

import Resources                from '../Resources';

const logger = new Logger('Datasets');

function createColumnDefs(classes,uctx) {
  const columnDefs = [
    { name: 'ResourceId', options: { display: true } },
    { name: 'ClientAccountId', options: { display: false } },
    { name: 'Description', options: { display: true } },
    { name: 'Metrics.Inventory.Latest.Data.TotalObjectBytes', label: 'Bytes', options: { 
      sortDescFirst: true,
      customBodyRender: fmtStorageBytes,
      setCellHeaderProps: () => {
        return {
          className: clsx({
            [classes.rightTableHead]: true,
          })
        };
      },
      setCellProps: () => ({align: "right"}),
      ...numericIntervalColumnFilterProps({label: 'Bytes'})
    }},
    { name: 'Metrics.Inventory.Latest.Data.TotalObjects', label: 'Objects', options: { 
      sortDescFirst: true,
      customBodyRender: fmtStorageObjects,
      setCellHeaderProps: () => {
        return {
          className: clsx({
            [classes.rightTableHead]: true,
          })
        };
      },
      setCellProps: () => ({align: "right"}),
      ...numericIntervalColumnFilterProps({label: 'Objects'})
    }},
    { name: 'Metrics.Activity.LastUpdate', label: 'Last Write', options: { 
      sortDescFirst: true,
      filter: false, 
      customBodyRender: fmtIso8601AsLocalDateHrsMin
    }},
    { name: 'Metrics.CloudWatch.Latest.Data.BytesDownloaded.Value', label: 'Dn.Vol', options: {
      hint: "The volume of data downloaded from this dataset in the past 24 hours.",
      sortDescFirst: true,
      filter: false, 
      customBodyRender: fmtStorageBytes,
      setCellHeaderProps: () => {
        return {
          className: clsx({
            [classes.rightTableHead]: true,
          })
        };
      },
      setCellProps: () => ({align: "right"}),
    }},
    { name: 'Metrics.CloudWatch.Latest.Data.BytesUploaded.Value', label: 'Up.Vol', options: { 
      hint: "The volume of data uploaded to this dataset in the past 24 hours.",
      sortDescFirst: true,
      filter: false, 
      customBodyRender: fmtStorageBytes,
      setCellHeaderProps: () => {
        return {
          className: clsx({
            [classes.rightTableHead]: true,
          })
        };
      },
      setCellProps: () => ({align: "right"}),
    }},
    { name: 'Metrics.CloudWatch.Latest.Data.Requests.Value', label: 'Requests', options: { 
      hint: "The total number of S3 API requests made against objects belonging to this dataset in the past 24 hours.",
      sortDescFirst: true,
      filter: false, 
      customBodyRender: fmtStorageObjects,
      setCellHeaderProps: () => {
        return {
          className: clsx({
            [classes.rightTableHead]: true,
          })
        };
      },
      setCellProps: () => ({align: "right"}),
    }},
    { name: 'Metrics.Crawl.Latest.Data.Completed', label: 'Last Crawl', options: {
      //hint: "The date and time of the last completed crawl of the data.",
      display: false,
      sortDescFirst: true,
      filter: false, 
      customBodyRender: fmtIso8601AsLocalDateHrsMin,
    }},
    { name: 'Metrics.Crawl.Latest.Data.Status', label: 'Crawl Status', options: { 
      //hint: "The status of the most recent crawl.",
      display: false,
      filterType: 'multiselect',
      customBodyRender: fmtCrawlStatus,
      customFilterListOptions: { render: v => `Crawl Status: ${v}` },
    }},
    { name: 'Metrics.Crawl.Latest.Data.RunTimeSecs', label: 'Crawl Time', options: { 
      //hint: "The status of the most recent crawl.",
      display: false,
      filter: false,
      sortDescFirst: true,
      setCellProps: () => ({align: "right"}),
      customBodyRender: fmtDaysHrsMinFromSecs,
    }},
    { name: 'Location.S3Uri', label: "Location", options: { 
      filter: false,
      //customBodyRender: fmtBucketRef 
    }},
    { name: 'CatalogConfiguration.Status', label: "Catalog" },
    { name: 'CatalogConfiguration.Cooldown', label: "Cool Down", 
      options: { 
        //hint: "The amount of time that must elapse since the last crawl before another crawl may occur.",
        display: false, 
        filter: false,
        setCellProps: () => ({align: "right"}),
        customBodyRender: fmtDaysHrsMinFromMins,
    }},
    { name: 'CatalogConfiguration.DatabaseName', label: "Database Name", options: { display: false, filter: false } },
    { name: 'SecurityConfiguration.SharingStatus', label: "Sharing", options: { display: false } },
    { name: 'SecurityConfiguration.AuthorizedReadOnlyAccountIds', label: "Readers", 
      options: { 
        //hint: "The accounts that have been authorized to read this database.",
        display: false,
        customBodyRender: fmtClientAcl,
        setCellHeaderProps: () => {
          return {
            className: clsx({
              [classes.rightTableHead]: true,
            })
          };
        },
        setCellProps: () => ({align: "right"}),
        customFilterListOptions: { render: v => `Readers: ${fmtClientAclPrincipal(v, uctx )}` },
        filterOptions: {
          renderValue: v => fmtClientAclPrincipal(v, uctx )
        },
        sortDescFirst: true,
        sortCompare: sortClientAclPrincipals,
    }},
    { name: 'CompactionConfiguration.Status', label: "Compaction", options: { display: false } },
    { name: 'ResourceType', options: { display: false, filter: false } },
    { name: 'StackUpdated', options: { display: false } },
  ];
  return columnDefs;
}

const handleGoToBucket = (bucket) => {
  window.open(fmtS3ConsoleLink(bucket),"_blank");
};

const handleCrawlControl = async (crawlAction, datasetId) => {
  const { succeeded, error } = crawlAction==='Start'?await startCrawl(datasetId):await stopCrawl(datasetId);
  if(succeeded) {
    logger.info(`${crawlAction} crawl succeeded on dataset ${datasetId}`);
  } else {
    logger.error(`${crawlAction} crawl failed on dataset ${datasetId}: ${error}`);
  }
};

const generateResourceActions = (resource) => {
  const resourceActions=[];
  if(resource.Location?.S3Uri) {
    resourceActions.push({ 
      name: `Go to Bucket`,
      icon: BucketIcon,
      onClick: () => handleGoToBucket(resource.Location?.S3Uri),
      external: true,
    });
  }
  if(resource.CatalogConfiguration?.Status==='ENABLED') {
    const crawlAction=resource.Metrics?.Crawl?.Latest?.Data?.Status==='RUNNING'?'Stop':'Start';
    resourceActions.push({ 
      name: `${crawlAction} Crawl`,
      icon: crawlAction==='Start'?StartCrawlIcon:StopCrawlIcon,
      onClick: () => handleCrawlControl(crawlAction, resource.ResourceId),
    });
  }
  return resourceActions;
};

export default function Datasets(props) {

  const { title, filter, columnDefs } = props;
  
  const uctx = React.useContext(UserContext);
  const classes = useStyles();
  const compiledColumnDefs = compileColumnDefs(createColumnDefs(classes,uctx),columnDefs);
 
  return (
    <Resources 
      type='Dataset'
      title={title}
      filter={filter}
      columnDefs={compiledColumnDefs}
      actions={generateResourceActions}
    />
  );
}

Datasets.propTypes = {
  columnDefs: PropTypes.array.isRequired,
  title: PropTypes.string,
  filter: PropTypes.func,
};

Datasets.defaultProps = {
  columnDefs: [],
};
